import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { trackEvent } from "../utils/analytics";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <nav
      aria-label="Main navigation"
      className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <picture>
                <source
                  srcSet="/images/logos/my-logo192.webp"
                  type="image/webp"
                />
                <img
                  src="/images/logos/my-logo192.png"
                  alt=""
                  aria-hidden="true"
                  width="32"
                  height="32"
                  className="h-8 w-8"
                />
              </picture>
              <span className="text-xl font-bold text-gray-800">
                StrikeAPrice
              </span>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
                className="flex items-center text-gray-700 hover:text-orange-600 transition-colors"
              >
                Solutions <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              <div
                role="menu"
                aria-label="Solutions menu"
                className={`absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 ${
                  isDropdownOpen ? "opacity-100 visible" : "opacity-0 invisible"
                }`}
              >
                <a
                  href="/dynamic-pricing"
                  onClick={() => setIsDropdownOpen(false)}
                  className="block px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-orange-600"
                >
                  Dynamic Pricing
                </a>
                <a
                  href="/competitor-monitoring"
                  onClick={() => setIsDropdownOpen(false)}
                  className="block px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-orange-600"
                >
                  Competitor Monitoring
                </a>
                <a
                  href="/marketplace-monitoring"
                  onClick={() => setIsDropdownOpen(false)}
                  className="block px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-orange-600"
                >
                  Marketplace Monitoring
                </a>
                <a
                  href="/data-extraction"
                  onClick={() => setIsDropdownOpen(false)}
                  className="block px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-orange-600"
                >
                  Data Extraction
                </a>
                <a
                  href="/map-alerts"
                  onClick={() => setIsDropdownOpen(false)}
                  className="block px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-orange-600"
                >
                  MAP Alerts
                </a>
              </div>
            </div>
            <Link
              to="/pricing"
              className="text-gray-700 hover:text-orange-600 transition-colors"
            >
              Pricing
            </Link>
            <Link
              to="/resources"
              className="text-gray-700 hover:text-orange-600 transition-colors"
            >
              Resources
            </Link>
            <Link
              to="/help"
              className="text-gray-700 hover:text-orange-600 transition-colors"
            >
              Help
            </Link>
            <Link
              to="/contact"
              className="text-gray-700 hover:text-orange-600 transition-colors"
            >
              Contact
            </Link>
            <Link
              to="/trial"
              className="btn-primary"
              onClick={() =>
                trackEvent("nav_cta_click", { location: "navbar" })
              }
            >
              Start Trial
            </Link>
          </div>

          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              className="text-gray-700 hover:text-orange-600"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {isOpen && (
          <div
            id="mobile-menu"
            role="navigation"
            aria-label="Mobile navigation"
            className="md:hidden"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
              <div className="px-3 py-2 text-gray-500 font-medium">
                Solutions
              </div>
              <Link
                to="/dynamic-pricing"
                onClick={() => setIsOpen(false)}
                className="block px-6 py-2 text-gray-700 hover:text-orange-600"
              >
                Dynamic Pricing
              </Link>
              <Link
                to="/competitor-monitoring"
                onClick={() => setIsOpen(false)}
                className="block px-6 py-2 text-gray-700 hover:text-orange-600"
              >
                Competitor Monitoring
              </Link>
              <Link
                to="/marketplace-monitoring"
                onClick={() => setIsOpen(false)}
                className="block px-6 py-2 text-gray-700 hover:text-orange-600"
              >
                Marketplace Monitoring
              </Link>
              <Link
                to="/data-extraction"
                onClick={() => setIsOpen(false)}
                className="block px-6 py-2 text-gray-700 hover:text-orange-600"
              >
                Data Extraction
              </Link>
              <Link
                to="/map-alerts"
                onClick={() => setIsOpen(false)}
                className="block px-6 py-2 text-gray-700 hover:text-orange-600"
              >
                MAP Alerts
              </Link>
              <Link
                to="/pricing"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-gray-700 hover:text-orange-600"
              >
                Pricing
              </Link>
              <Link
                to="/resources"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-gray-700 hover:text-orange-600"
              >
                Resources
              </Link>
              <Link
                to="/help"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-gray-700 hover:text-orange-600"
              >
                Help
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-2 text-gray-700 hover:text-orange-600"
              >
                Contact
              </Link>
              <Link
                to="/trial"
                onClick={() => {
                  setIsOpen(false);
                  trackEvent("nav_cta_click", { location: "mobile_navbar" });
                }}
                className="block px-3 py-2 btn-primary text-center mx-3 mt-2"
              >
                Start Trial
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
